"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import { getSession } from "@/lib/auth/cookies";
import { withTenant } from "@/lib/auth/with-tenant";
import { getAccessPrincipal } from "@/lib/documents/access";
import {
  DOCUMENT_ACL_INCLUDE,
  hasEffectiveDocumentPermission,
  type AccessPrincipal,
} from "@/lib/documents/acl";
import { canAssignAnyDepartment, nextHomeDepartmentAcls, parseHomeDepartmentId } from "@/lib/documents/home";
import { cancelActiveIndexingJobs } from "@/lib/documents/index-cancel";
import { clearIndexProgressData } from "@/lib/documents/index-progress";
import {
  hasActiveReindex,
  indexingVersion,
  selectedDocumentIds,
} from "@/lib/documents/reindex";
import { prisma } from "@/lib/db";
import { parseCollectionId } from "@/lib/documents/collection";
import { documentCollectionCopy, documentCollectionErrorMessage } from "@/lib/ui/document-collection";
import { documentHomeErrorMessage } from "@/lib/ui/document-home";
import { isUniqueNameError, parseOrgName } from "@/lib/ui/org";
import { reindexCopy, indexStageCopy, parseDocumentTitle } from "@/lib/ui/document-view";
import {
  documentIndexLive,
  type DocumentIndexLiveRow,
} from "@/lib/ui/document-index-live";
import {
  executeUploadDocument,
  executeUploadVersion,
} from "@/lib/documents/upload-request";
import { readDisplayTimeZone } from "@/lib/ui/timezone-cookie";
export type UploadState = {
  error: string | null;
};

export async function uploadDocumentAction(
  _prev: UploadState,
  formData: FormData,
): Promise<UploadState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти" };
  }

  const created = await executeUploadDocument(session, formData);
  if (created.error) {
    return { error: created.error };
  }

  revalidatePath("/documents");
  redirect(`/documents/${created.id}`);
}

export async function uploadVersionAction(
  documentId: string,
  _prev: UploadState,
  formData: FormData,
): Promise<UploadState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти" };
  }

  const result = await executeUploadVersion(session, documentId, formData);
  if (result.error) {
    return { error: result.error };
  }

  revalidatePath(`/documents/${documentId}`);
  revalidatePath("/documents");
  return { error: null };
}

export type ReindexState = {
  error: string | null;
  queued: boolean;
};

export type BatchReindexState = {
  error: string | null;
  queued: number;
};

async function enqueueDocumentReindex(params: {
  tenantId: string;
  userId: string;
  principal: AccessPrincipal;
  documentId: string;
  /** all | text (parse/OCR) | vectors | facts */
  stage?: "all" | "text" | "vectors" | "facts";
}): Promise<{ error: string | null }> {
  const stage = params.stage ?? "all";
  const document = await prisma.document.findUnique({
    where: { id: params.documentId },
    include: DOCUMENT_ACL_INCLUDE,
  });

  if (!document) {
    return { error: "Документ не найден" };
  }

  if (!hasEffectiveDocumentPermission(params.principal, document, "WRITE")) {
    return { error: "Нет прав на обновление индекса" };
  }

  const current = document.currentVersionId
    ? await prisma.documentVersion.findUnique({
        where: { id: document.currentVersionId },
      })
    : await prisma.documentVersion.findFirst({
        where: { documentId: document.id },
        orderBy: { version: "desc" },
      });

  if (!current) {
    return { error: "Нет версии для индекса" };
  }

  const jobs = await prisma.ingestionJob.findMany({
    where: { documentVersionId: current.id },
    select: { status: true },
  });
  if (hasActiveReindex(jobs.map((job) => job.status))) {
    return { error: reindexCopy.already };
  }

  const hasText = Boolean(current.extractedText?.trim());
  if ((stage === "vectors" || stage === "facts") && !hasText) {
    return {
      error:
        stage === "vectors"
          ? indexStageCopy.vectorsNeedText
          : indexStageCopy.factsNeedText,
    };
  }

  await prisma.document.update({
    where: { id: document.id },
    data: { status: "PROCESSING" },
  });

  if (stage === "facts") {
    await prisma.documentVersion.update({
      where: { id: current.id },
      data: {
        extractStatus: "PENDING",
        extractError: null,
        ...clearIndexProgressData(),
      },
    });
    await prisma.ingestionJob.create({
      data: {
        tenantId: params.tenantId,
        documentVersionId: current.id,
        createdById: params.userId,
        status: "PENDING",
        fullIndex: true,
        wikiOnly: true,
        embedOnly: false,
      },
    });
    return { error: null };
  }

  if (stage === "vectors") {
    await prisma.documentVersion.update({
      where: { id: current.id },
      data: {
        vectorIndexedAt: null,
        ...clearIndexProgressData(),
      },
    });
    await prisma.ingestionJob.create({
      data: {
        tenantId: params.tenantId,
        documentVersionId: current.id,
        createdById: params.userId,
        status: "PENDING",
        fullIndex: true,
        wikiOnly: false,
        embedOnly: true,
      },
    });
    return { error: null };
  }

  const forceReparse = stage === "text";
  await prisma.documentVersion.update({
    where: { id: current.id },
    data: {
      parseStatus: "PENDING",
      parseError: null,
      ...(forceReparse ? { extractedText: null } : {}),
      ...clearIndexProgressData(),
    },
  });
  await prisma.ingestionJob.create({
    data: {
      tenantId: params.tenantId,
      documentVersionId: current.id,
      createdById: params.userId,
      status: "PENDING",
      // text-only parse; "all" runs embeddings + Wiki after parse
      fullIndex: stage === "all",
      wikiOnly: false,
      embedOnly: false,
    },
  });

  return { error: null };
}

export async function reindexDocumentAction(
  documentId: string,
  _prev: ReindexState,
  _formData: FormData,
): Promise<ReindexState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", queued: false };
  }

  const result = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    return enqueueDocumentReindex({
      tenantId: session.tenantId,
      userId: session.userId,
      principal,
      documentId,
      stage: "all",
    });
  });

  if (result.error) {
    return { error: result.error, queued: false };
  }

  revalidatePath(`/documents/${documentId}`);
  revalidatePath("/documents");
  return { error: null, queued: true };
}

export async function indexStageDocumentAction(
  documentId: string,
  stage: "text" | "vectors" | "facts",
  _prev: ReindexState,
  _formData: FormData,
): Promise<ReindexState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", queued: false };
  }

  const result = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    return enqueueDocumentReindex({
      tenantId: session.tenantId,
      userId: session.userId,
      principal,
      documentId,
      stage,
    });
  });

  if (result.error) {
    return { error: result.error, queued: false };
  }

  revalidatePath(`/documents/${documentId}`);
  revalidatePath("/documents");
  return { error: null, queued: true };
}

export async function reindexDocumentsAction(
  _prev: BatchReindexState,
  formData: FormData,
): Promise<BatchReindexState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", queued: 0 };
  }

  const ids = selectedDocumentIds(formData);
  if (ids.length === 0) {
    return { error: reindexCopy.none, queued: 0 };
  }

  const result = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    let queued = 0;
    let skippedActive = 0;
    for (const documentId of ids) {
      const item = await enqueueDocumentReindex({
        tenantId: session.tenantId,
        userId: session.userId,
        principal,
        documentId,
      });
      if (!item.error) {
        queued += 1;
        continue;
      }
      if (item.error === reindexCopy.already) {
        skippedActive += 1;
      }
    }
    return { queued, skippedActive };
  });

  if (result.queued === 0) {
    return {
      error: result.skippedActive > 0 ? reindexCopy.already : reindexCopy.none,
      queued: 0,
    };
  }

  revalidatePath("/documents");
  return { error: null, queued: result.queued };
}

export async function cancelIndexingAction() {
  const session = await getSession();
  if (!session) {
    return;
  }

  await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    await cancelActiveIndexingJobs({
      tenantId: session.tenantId,
      principal,
    });
  });

  revalidatePath("/documents");
  revalidatePath("/");
}

export type CollectionFormState = {
  error: string | null;
  saved: boolean;
};

export async function saveDocumentTitleAction(
  documentId: string,
  _prev: CollectionFormState,
  formData: FormData,
): Promise<CollectionFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }

  const parsed = parseDocumentTitle(String(formData.get("title") ?? ""));
  if (!parsed.ok) {
    return { error: parsed.error, saved: false };
  }

  const error = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    const document = await prisma.document.findUnique({
      where: { id: documentId },
      include: DOCUMENT_ACL_INCLUDE,
    });
    if (!document || !hasEffectiveDocumentPermission(principal, document, "WRITE")) {
      return "Нет доступа";
    }
    await prisma.document.update({
      where: { id: document.id },
      data: { title: parsed.value, titleSource: "MANUAL" },
    });
    return null;
  });

  if (error) {
    return { error, saved: false };
  }

  revalidatePath("/documents");
  revalidatePath(`/documents/${documentId}`);
  return { error: null, saved: true };
}

export async function createCollectionAction(
  _prev: CollectionFormState,
  formData: FormData,
): Promise<CollectionFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }

  const parsed = parseOrgName(String(formData.get("name") ?? ""));
  if (!parsed.ok) {
    return { error: parsed.error, saved: false };
  }

  const documentId = String(formData.get("documentId") ?? "").trim();

  const error = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    let document: { id: string } | null = null;
    if (documentId) {
      const row = await prisma.document.findUnique({
        where: { id: documentId },
        include: DOCUMENT_ACL_INCLUDE,
      });
      if (!row || !hasEffectiveDocumentPermission(principal, row, "WRITE")) {
        return "Нет доступа";
      }
      document = row;
    }
    try {
      await prisma.$transaction(async (tx) => {
        const collection = await tx.collection.create({
          data: { tenantId: session.tenantId, name: parsed.value },
        });
        if (document) {
          await tx.document.update({
            where: { id: document.id },
            data: { collectionId: collection.id },
          });
        }
      });
      return null;
    } catch (cause) {
      return isUniqueNameError(cause)
        ? documentCollectionCopy.nameTaken
        : "Не удалось создать коллекцию";
    }
  });

  if (error) {
    return { error, saved: false };
  }

  revalidatePath("/documents");
  if (documentId) {
    revalidatePath(`/documents/${documentId}`);
  }
  return { error: null, saved: true };
}

export async function saveDocumentCollectionAction(
  documentId: string,
  _prev: CollectionFormState,
  formData: FormData,
): Promise<CollectionFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }

  const error = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    const document = await prisma.document.findUnique({
      where: { id: documentId },
      include: DOCUMENT_ACL_INCLUDE,
    });
    if (!document || !hasEffectiveDocumentPermission(principal, document, "WRITE")) {
      return "Нет доступа";
    }
    const collections = await prisma.collection.findMany({
      select: { id: true },
    });
    const collection = parseCollectionId(
      String(formData.get("collectionId") ?? ""),
      collections.map((item) => item.id),
    );
    if (!collection.ok) {
      return documentCollectionErrorMessage(collection.error);
    }
    await prisma.document.update({
      where: { id: document.id },
      data: { collectionId: collection.value },
    });
    return null;
  });

  if (error) {
    return { error, saved: false };
  }

  revalidatePath("/documents");
  revalidatePath(`/documents/${documentId}`);
  return { error: null, saved: true };
}

export async function saveDocumentDepartmentAction(
  documentId: string,
  _prev: CollectionFormState,
  formData: FormData,
): Promise<CollectionFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }

  const error = await withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    const document = await prisma.document.findUnique({
      where: { id: documentId },
      include: DOCUMENT_ACL_INCLUDE,
    });
    if (!document || !hasEffectiveDocumentPermission(principal, document, "WRITE")) {
      return "Нет доступа";
    }
    const allowedDepartments = canAssignAnyDepartment(session.roles)
      ? await prisma.department.findMany({ select: { id: true } })
      : principal.departmentIds.length === 0
        ? []
        : await prisma.department.findMany({
            where: { id: { in: principal.departmentIds } },
            select: { id: true },
          });
    const allowedIds = allowedDepartments.map((item) => item.id);
    if (document.departmentId) {
      allowedIds.push(document.departmentId);
    }
    const departmentId = parseHomeDepartmentId(
      String(formData.get("departmentId") ?? ""),
    );
    if (departmentId && !allowedIds.includes(departmentId)) {
      return documentHomeErrorMessage("unknown-department");
    }
    if (departmentId === document.departmentId) {
      return null;
    }
    const plan = nextHomeDepartmentAcls({
      acls: document.acls,
      previousDepartmentId: document.departmentId,
      nextDepartmentId: departmentId,
    });
    await prisma.$transaction(async (tx) => {
      if (plan.deleteAclIds.length > 0) {
        await tx.documentAcl.deleteMany({
          where: { id: { in: plan.deleteAclIds } },
        });
      }
      if (plan.updateAclId && departmentId) {
        await tx.documentAcl.update({
          where: { id: plan.updateAclId },
          data: { principalId: departmentId },
        });
      }
      if (plan.createWrite && departmentId) {
        await tx.documentAcl.create({
          data: {
            tenantId: session.tenantId,
            documentId: document.id,
            principalType: "DEPARTMENT",
            principalId: departmentId,
            permission: "WRITE",
          },
        });
      }
      await tx.document.update({
        where: { id: document.id },
        data: { departmentId },
      });
    });
    return null;
  });

  if (error) {
    return { error, saved: false };
  }

  revalidatePath("/documents");
  revalidatePath(`/documents/${documentId}`);
  return { error: null, saved: true };
}

export async function listDocumentIndexLiveAction(): Promise<
  DocumentIndexLiveRow[]
> {
  const session = await getSession();
  if (!session) {
    return [];
  }

  return withTenant(async () => {
    const principal = await getAccessPrincipal(session);
    const rows = await prisma.document.findMany({
      select: {
        id: true,
        status: true,
        currentVersionId: true,
        acls: true,
        collection: { select: { acls: true } },
        versions: {
          select: {
            id: true,
            parseStatus: true,
            vectorIndexedAt: true,
            extractStatus: true,
            extractError: true,
            indexStage: true,
            indexDone: true,
            indexTotal: true,
            indexStageAt: true,
            indexBeatAt: true,
            jobs: {
              select: {
                status: true,
                fullIndex: true,
                wikiOnly: true,
                embedOnly: true,
                createdAt: true,
                updatedAt: true,
              },
            },
          },
        },
      },
    });

    const timeZone = await readDisplayTimeZone();

    return rows
      .filter((row) => hasEffectiveDocumentPermission(principal, row, "READ"))
      .map((row) => {
        const current =
          indexingVersion(row.versions, row.currentVersionId) ??
          row.versions[0];
        return {
          id: row.id,
          ...documentIndexLive({
            status: row.status,
            parseStatus: current?.parseStatus ?? "PENDING",
            vectorIndexedAt: current?.vectorIndexedAt ?? null,
            extractStatus: current?.extractStatus ?? "PENDING",
            extractError: current?.extractError ?? null,
            indexStage: current?.indexStage ?? null,
            indexDone: current?.indexDone ?? 0,
            indexTotal: current?.indexTotal ?? 0,
            indexStageAt: current?.indexStageAt ?? null,
            indexBeatAt: current?.indexBeatAt ?? null,
            jobStatuses: current?.jobs.map((job) => job.status) ?? [],
            jobs: current?.jobs ?? [],
            timeZone,
          }),
        };
      });
  });
}
